import { useState, useEffect } from "react";
import {
  Users, Plus, Edit2, Trash2, X, Search, RefreshCw,
  Phone, Mail, Clock, DollarSign, ChevronDown,
} from "lucide-react";
import { useAdmin } from "../admin-context";

const ROLES   = ["manager", "chef", "cashier", "delivery", "staff"];
const SHIFTS  = ["morning", "afternoon", "evening", "night"];
const STATUSES = ["active", "inactive", "on_leave"];

const ROLE_COLORS = {
  manager:  "bg-purple-100 text-purple-800",
  chef:     "bg-orange-100 text-orange-700",
  cashier:  "bg-blue-100 text-blue-800",
  delivery: "bg-green-100 text-green-800",
  staff:    "bg-gray-100 text-gray-700",
};
const STATUS_COLORS = {
  active:   "bg-emerald-100 text-emerald-800",
  inactive: "bg-gray-100 text-gray-500",
  on_leave: "bg-yellow-100 text-yellow-800",
};

const Drawer = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
};

const Field = ({ label, children, required }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const inp = "w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300";
const sel = "w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", role: "staff", status: "active", shift: "morning", hire_date: "", hourly_rate: "" };

const StaffPage = () => {
  const { staffList, loadStaff, createStaff, updateStaff, deleteStaff, showToast } = useAdmin();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadStaff(); }, []);

  const F = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openAdd  = () => { setEditing(null); setForm(EMPTY); setDrawerOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...s, hire_date: s.hire_date?.split("T")[0] || "" }); setDrawerOpen(true); };

  const handleSave = async () => {
    if (!form.first_name.trim()) return showToast("First name is required", "error");
    setSaving(true);
    if (editing) await updateStaff(editing.id, form);
    else         await createStaff(form);
    setSaving(false);
    setDrawerOpen(false);
    showToast(editing ? "Staff updated" : "Staff member added");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this staff member?")) return;
    await deleteStaff(id);
    showToast("Removed");
  };

  const list = (staffList || []).filter((s) => {
    if (roleFilter !== "all" && s.role !== roleFilter) return false;
    const q = search.toLowerCase();
    if (q && !`${s.first_name} ${s.last_name} ${s.email}`.toLowerCase().includes(q)) return false;
    return true;
  });

  const activeCount = (staffList || []).filter((s) => s.status === "active").length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Staff & Employees</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your team, roles, and shifts</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => loadStaff()} className="flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            <RefreshCw size={15} /> Refresh
          </button>
          <button onClick={openAdd} className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600">
            <Plus size={16} /> Add Staff
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Total Staff",  value: (staffList || []).length, color: "bg-blue-100 text-blue-600" },
          { label: "Active",       value: activeCount,               color: "bg-green-100 text-green-600" },
          { label: "On Leave",     value: (staffList || []).filter((s) => s.status === "on_leave").length, color: "bg-yellow-100 text-yellow-600" },
          { label: "Inactive",     value: (staffList || []).filter((s) => s.status === "inactive").length, color: "bg-gray-100 text-gray-500" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border p-4">
            <p className="text-xs text-gray-500">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 inline-block px-2 py-0.5 rounded-lg ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search staff..."
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
        </div>
        {["all", ...ROLES].map((r) => (
          <button key={r} onClick={() => setRoleFilter(r)}
            className={`px-3 py-2 rounded-lg text-sm font-medium capitalize border transition ${roleFilter === r ? "bg-orange-500 text-white border-orange-500" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
            {r === "all" ? "All Roles" : r}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {["Name", "Role", "Shift", "Contact", "Hired", "Status", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {list.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-400">No staff members found</td></tr>
              ) : list.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-700 font-bold text-sm flex items-center justify-center flex-shrink-0">
                        {s.first_name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{s.first_name} {s.last_name}</p>
                        {s.hourly_rate > 0 && (
                          <p className="text-xs text-gray-400">${Number(s.hourly_rate).toFixed(2)}/hr</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${ROLE_COLORS[s.role] || "bg-gray-100 text-gray-700"}`}>{s.role}</span>
                  </td>
                  <td className="px-4 py-3 capitalize text-gray-600">{s.shift}</td>
                  <td className="px-4 py-3">
                    <div className="space-y-0.5">
                      {s.phone && <div className="flex items-center gap-1 text-xs text-gray-500"><Phone size={11} />{s.phone}</div>}
                      {s.email && <div className="flex items-center gap-1 text-xs text-gray-500"><Mail size={11} />{s.email}</div>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {s.hire_date ? new Date(s.hire_date).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[s.status] || ""}`}>
                      {s.status?.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => openEdit(s)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"><Edit2 size={14} /></button>
                      <button onClick={() => handleDelete(s.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-400"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drawer */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={editing ? "Edit Staff Member" : "Add Staff Member"}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First Name" required>
              <input value={form.first_name} onChange={F("first_name")} placeholder="John" className={inp} />
            </Field>
            <Field label="Last Name">
              <input value={form.last_name} onChange={F("last_name")} placeholder="Doe" className={inp} />
            </Field>
          </div>
          <Field label="Email">
            <input type="email" value={form.email} onChange={F("email")} placeholder="john@example.com" className={inp} />
          </Field>
          <Field label="Phone">
            <input value={form.phone} onChange={F("phone")} placeholder="+1 555 000 0000" className={inp} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Role">
              <select value={form.role} onChange={F("role")} className={sel}>
                {ROLES.map((r) => <option key={r} value={r} className="capitalize">{r}</option>)}
              </select>
            </Field>
            <Field label="Shift">
              <select value={form.shift} onChange={F("shift")} className={sel}>
                {SHIFTS.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Hire Date">
              <input type="date" value={form.hire_date} onChange={F("hire_date")} className={inp} />
            </Field>
            <Field label="Hourly Rate ($)">
              <input type="number" min="0" step="0.01" value={form.hourly_rate} onChange={F("hourly_rate")} placeholder="0.00" className={inp} />
            </Field>
          </div>
          <Field label="Status">
            <select value={form.status} onChange={F("status")} className={sel}>
              {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s.replace("_", " ")}</option>)}
            </select>
          </Field>
          <Field label="Notes">
            <textarea value={form.notes || ""} onChange={F("notes")} rows={2} placeholder="Additional notes..."
              className={`${inp} resize-none`} />
          </Field>
          <button onClick={handleSave} disabled={saving}
            className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition disabled:opacity-60">
            {saving ? "Saving..." : editing ? "Update" : "Add Staff Member"}
          </button>
        </div>
      </Drawer>
    </div>
  );
};

export default StaffPage;
